import { mapGetters, mapActions } from "vuex";
import { OAPI_API_TYPE } from "@/../app-constants";
import {
  ROUTE_NAMES,
  showDialog,
  MODAL,
  DISPLAY,
  ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE,
  SELECT_PICK_UP_TIME_PROMPT
} from "@/utils";
import PickupTimeWrapper from "@/components/modal/pickup-time-wrapper/PickupTimeWrapper.vue";
import { useOrderAheadMapper } from "@/store/modules/orderAhead/mapper";

const { mapToEstimatedOrderTime } = useOrderAheadMapper();

const pickupTimeMixin = {
  computed: {
    ...mapGetters({
      getValidPickupTimes: "vendor/getValidPickupTimesByVendorId",
      getValidTodayPickupTimes: "vendor/getValidPickupTimesForTodayByVendorId",
      isVendorOpenByVendorId: "vendor/isVendorOpenByVendorId",
      isPickupTimeSelectionEnableByVendorId: "vendor/isPickupTimeSelectionEnableByVendorId",
      getPickupTimes: "vendor/getPickupTimesByVendorId",
      activeVendor: "vendor/getActiveVendor",
      activeVenue: "venue/getVenueInfo",
      APIType: "venueSettings/APIType",
      selectedPickup: "orderAhead/selectedPickup",
      getPickupEstimatedTimeRange: "vendor/getPickupEstimatedTimeRange",
      pickupTimeAutoSelectEnabled: "vendor/pickupTimeAutoSelectEnabled",
      getVendorsList: "vendor/getVendorsList"
    }),
    /**
     * This method returns if the venue allow order ahead feature
     *
     * @return {boolean}
     */
    isVenueOrderAheadAllowed() {
      return this.APIType === OAPI_API_TYPE;
    },
    /**
     * This method returns the correct text to display depending the pickup time selected by the user
     *
     * @return {string}
     */
    selectedPickupOption() {
      if (!this.isAnyPickupAvailable(this.activeVendor.id)) return "Not currently accepting orders.";

      const pickupTimes = this.getValidPickupTimes(this.activeVendor.id);
      // Setting to it's own variable since state could cause a race condition
      // if it's not updated before calculating is it's ASAP
      const pickupTime = this.selectedPickup;

      if (!pickupTime) {
        return SELECT_PICK_UP_TIME_PROMPT;
      }

      if (!pickupTimes.includes(pickupTime)) {
        return ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE;
      }

      const isNowAvailable = Boolean(pickupTimes[0] === pickupTime);
      const scheduledTime = mapToEstimatedOrderTime(pickupTime, this.activeVenue.timezone);

      return isNowAvailable ? `ASAP (Ready ${scheduledTime})` : `Ready ${scheduledTime}`;
    }
  },
  methods: {
    ...mapActions({
      fetchVendorById: "vendor/fetchVendorById",
      setSelectedPickup: "orderAhead/setSelectedPickup",
      setActiveDayPart: "vendor/setActiveDayPart",
      fetchVendorPickupTimes: "vendor/fetchVendorPickupTimes"
    }),
    /**
     * This method indicates if the vendor is open or has future pickup time slots available
     *
     * @param {number} vendorId
     * @return {boolean}
     */
    isAnyPickupAvailable(vendorId) {
      return !!this.getValidTodayPickupTimes(vendorId)?.length;
    },
    /**
     * This method displays the right modal for pickup time.
     * If there is an option to pick up, this method shows the regular pickup time Modal
     * If there is no option available, this method shows a dialog allowing to browse the menu
     *
     * @param {number} vendorId
     * @param {PickupTimeWrapperProps} pickupProps
     * @param {autoSelectPickupTime} autoSelectPickupTime do not display the pick up modal and selects the
     */
    async showPickupTimeOptions(vendorId, pickupProps, autoSelectPickupTime = false, showNoOrdersDialog = true) {
      //hydrate store with vendor pickup times
      await this.fetchVendorPickupTimes({ vendorId });

      // If there is an option to pick up
      if (this.isAnyPickupAvailable(vendorId)) {
        const pickupTimes = this.getValidTodayPickupTimes(vendorId);

        // If more than one option is available or auto select is not desired, show the regular pickup time Modal
        if (pickupTimes.length > 1 || !autoSelectPickupTime) return this.showPickupTimeWrapperModal(pickupProps);

        // Only one option is available and auto select is desired select the only one available
        this.setSelectedPickup({ time: pickupTimes[0], id: vendorId });
        this.setActiveDayPart();
        pickupProps.goToNextStep?.();
        return;
      }

      // If there is no option available and showNoOrdersDialog is true, show a dialog allowing to browse the menu
      if (showNoOrdersDialog) this.showNoOrdersDialog(pickupProps);
    },
    /**
     * This method displays the regular pickup time Modal
     *
     * @param {PickupTimeWrapperProps} pickupProps
     */
    showPickupTimeWrapperModal(pickupProps) {
      const baseModalProps = {
        name: "PickupTimeWrapper",
        adaptive: true,
        scrollable: true
      };
      const desktopModalProps = {
        height: "auto",
        width: 500,
        classes: "modal--dialog"
      };
      const mobileModalProps = {
        height: "100%",
        width: "100%"
      };

      const isDesktop = window.innerWidth >= DISPLAY.minDesktopWidth;
      const modalProps = isDesktop ? { ...baseModalProps, ...desktopModalProps } : { ...baseModalProps, ...mobileModalProps };

      this.$modal.show(PickupTimeWrapper, pickupProps, modalProps);
    },
    /**
     * This method displays a dialog allowing to browse the menu.
     *
     * @param {PickupTimeWrapperProps} pickupProps
     */
    showNoOrdersDialog(pickupProps) {
      const modalButtons = [];
      /**
       * logoNavigation: 1 => Active Vendor > Menu page
       * logoNavigation: 0 => Vendor Selection Page
       */
      const allowVendorPicker = (this.activeVendor?.logoNavigation || 0) === 0;
      const routeIsVendorPicker = this.$route?.name === ROUTE_NAMES.VENDOR_PICKER;

      if (allowVendorPicker && !routeIsVendorPicker && this.getVendorsList?.length > 1) {
        const onOtherVendors = () => {
          this.$modal.hide(MODAL.dialogName);
          this.$router.push({ name: ROUTE_NAMES.VENDOR_PICKER });
        };
        modalButtons.push({
          title: "Other Vendors",
          handler: onOtherVendors
        });
      }

      const onBrowseMenu = () => {
        this.$modal.hide(MODAL.dialogName);
        pickupProps?.goToNextStep();
      };
      modalButtons.push({
        title: "Browse Menu",
        handler: onBrowseMenu
      });

      showDialog(this, "", "This location is not currently accepting orders.", modalButtons, routeIsVendorPicker);
    },

    /**
     * This method check if the vendor has order ahead on and if it has
     * check if the selected pickup time is still valid
     *
     */
    async isSelectedPickupTimeStillValid() {
      if (!this.isVenueOrderAheadAllowed || !this.isPickupTimeSelectionEnableByVendorId(this.activeVendor.id)) return true;
      if (!this.selectedPickup) return false;

      const vendorPickupTimes = await this.fetchVendorPickupTimes({ vendorId: this.activeVendor.id });

      return !!vendorPickupTimes.find((pickupTime) => pickupTime === this.selectedPickup);
    }
  }
};

export default pickupTimeMixin;
